import { LATE_MINUTES, laneOf, minutesSince } from './kitchen';
import type { Order } from './types';

/** What a table is doing right now: shared by the tile view and the 3D view. */
export type TableState = 'free' | 'seated' | 'cooking' | 'eating' | 'bill' | 'late';

export const STATE_LABEL: Record<TableState, string> = {
  free: 'ว่าง · Free', seated: 'นั่งแล้ว · Seated', cooking: 'รออาหาร · Cooking',
  eating: 'กำลังทาน · Eating', bill: 'เช็คบิล · Bill', late: 'ช้า · Late',
};
/** Marker colours in 3D (also distinct in lightness, not hue alone). */
export const STATE_COLOR: Record<TableState, string> = {
  free: '#9AA1B8', seated: '#4F86F0', cooking: '#FFB21E', eating: '#2FB36B', bill: '#7C5CFF', late: '#E5484D',
};

export interface SessionLike { id: string; guests?: number; openedAt: string; billRequestedAt?: string }

export interface TableSummary {
  state: TableState;
  guests: number;
  minutes: number;
  orderIds: string[];
  /** Dishes still to come out of the kitchen (queued or cooking). */
  cooking: number;
  /** Dishes waiting at the pass to be carried to the table. */
  ready: number;
  served: number;
  /** Item ids of dishes on the table (served), one per plate, oldest first: the 3D view draws their stickers. */
  onTable: string[];
  totalSatang: number;
  billRequested: boolean;
}

/**
 * `orders` = every order of the sitting (served ones included), so a table whose food has all arrived reads as
 * "eating" rather than falling back to "seated".
 */
export function summarize(session: SessionLike | null | undefined, orders: Order[], now: number): TableSummary {
  if (!session) return { state: 'free', guests: 0, minutes: 0, orderIds: [], cooking: 0, ready: 0, served: 0, onTable: [], totalSatang: 0, billRequested: false };
  const sorted = [...orders].sort((a, b) => a.placedAt.localeCompare(b.placedAt));
  const lines = sorted.flatMap((o) => o.lines.filter((l) => l.status !== 'cancelled'));
  const count = (st: string[]) => lines.filter((l) => st.includes(l.status)).reduce((n, l) => n + l.qty, 0);
  const inKitchen = sorted.filter((o) => { const lane = laneOf(o); return lane === 'new' || lane === 'cooking'; });
  const late = inKitchen.some((o) => minutesSince(o.placedAt, now) >= LATE_MINUTES);
  const billRequested = !!session.billRequestedAt;
  const served = count(['served']);
  const ready = count(['ready']);
  const state: TableState = late ? 'late' : billRequested ? 'bill' : inKitchen.length ? 'cooking' : served || ready ? 'eating' : 'seated';
  return {
    state, guests: session.guests ?? 0, minutes: minutesSince(session.openedAt, now),
    orderIds: sorted.map((o) => o.id),
    cooking: count(['queued', 'cooking']), ready, served,
    onTable: lines.filter((l) => l.status === 'served').flatMap((l) => Array<string>(Math.min(l.qty, 6)).fill(l.itemId)),
    totalSatang: lines.reduce((n, l) => n + l.unitSatang * l.qty, 0),
    billRequested,
  };
}

export type FloatKind = 'seated' | 'order' | 'ready' | 'bill' | 'paid';
export interface FloatUp { kind: FloatKind; text: string }

/** An order counts as "just placed" for a float-up only within this window (older ones are history loading in). */
export const FRESH_ORDER_MS = 120_000;

/** Things that just happened at a table, to float up from it in 3D. Nothing on first sight (prev undefined). */
export function happenings(prev: TableSummary | undefined, next: TableSummary, orders: Order[], now = Date.now()): FloatUp[] {
  if (!prev) return [];
  const out: FloatUp[] = [];
  if (prev.state === 'free' && next.state !== 'free') out.push({ kind: 'seated', text: next.guests ? `นั่งแล้ว ${next.guests} ท่าน · Seated` : 'นั่งแล้ว · Seated' });
  if (prev.state !== 'free' && next.state === 'free') out.push({ kind: 'paid', text: 'ปิดโต๊ะ · Table cleared' });
  const fresh = orders.filter((o) => !prev.orderIds.includes(o.id) && now - Date.parse(o.placedAt) < FRESH_ORDER_MS);
  const freshDishes = fresh.reduce((n, o) => n + o.lines.reduce((m, l) => m + l.qty, 0), 0);
  if (fresh.length && next.state !== 'free') out.push({ kind: 'order', text: `+${freshDishes} สั่งใหม่ · New order` });
  if (next.ready > prev.ready) out.push({ kind: 'ready', text: `${next.ready} พร้อมเสิร์ฟ · Ready` });
  if (next.billRequested && !prev.billRequested) out.push({ kind: 'bill', text: 'ขอเช็คบิล · Bill please' });
  return out;
}

/** Tables that want a person now: drives the per-room badges. */
export const needsAttention = (s: TableSummary) => s.state === 'bill' || s.state === 'late' || s.ready > 0;

/** Evening = after sunset in Bangkok (18:00-06:00), when the 3D floor dims and lamps glow. */
export function isEvening(now: number): boolean {
  const h = new Date(now + 7 * 3600_000).getUTCHours();
  return h >= 18 || h < 6;
}
